import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { FlexReportUnregisteredTableData } from 'libs/domain';
import { Registration } from 'libs/domain';
import { ACTIVITY_RESTRICTION_ERRORS_TITLES, RESTRICTION_ERRORS, RestrictionErrorMinimal } from 'libs/domain';
import { flex_time_activity_pb as ft_activity_pb } from 'libs/generated-grpc-web';

import { RootService } from '@app/src/app/minimal/services/RootService';

import { FtmReportsUnRegMessages } from '@modules/flex-time-manager/components/ftm-reports/components/ftm-reports-unreg/ftm-reports-unreg.constants';
import { PeopleSelectorService, PsData, PsSummaryErrorsData } from '@modules/people-selector';
import { PeopleRoute } from '@modules/people/types';

import {
  SystemAlertCloseEvents,
  SystemAlertModalService,
  SystemAlertModalType,
} from '@shared/components/system-alert-modal';
import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';
import { UserListFilterService } from '@shared/components/user-list-filter';
import { FlexTimePermissionsService, FlexTimeRegistrationService } from '@shared/services/flex-time';

type FlexActionInfo = {
  type: 'assign' | 'register';
  verb: string;
  action: string;
};

@Injectable({
  providedIn: 'root',
})
export class FlexTimeRegistrationValidationService {
  constructor(
    public snackbar: SystemAlertSnackBarService,
    public alertModal: SystemAlertModalService,
    private _rootService: RootService,
    public _peopleSelector: PeopleSelectorService,
    private _ftRegistration: FlexTimeRegistrationService,
    private _flexPermissions: FlexTimePermissionsService,
    private _router: Router,
    private _userLists: UserListFilterService,
  ) {}

  public async assign({
    activityId,
    periodId,
    bypassRestrictions = false,
    hashes,
    selectedPeople,
    canOverride,
  }: {
    activityId?: number;
    periodId?: number;
    bypassRestrictions?: boolean;
    hashes?: string[];
    selectedPeople?: PsData[] | FlexReportUnregisteredTableData[];
    canOverride?: boolean;
  }): Promise<void> {
    const actionInfo: FlexActionInfo = {
      type: 'assign',
      verb: 'Assign',
      action: 'Assignment',
    };

    return this._doFlexAction({
      actionInfo,
      activityId,
      periodId,
      bypassRestrictions,
      hashes,
      selectedPeople,
      canOverride,
    });
  }

  public async register({
    activityId,
    periodId,
    bypassRestrictions = false,
    hashes,
    selectedPeople,
    canOverride,
  }: {
    activityId?: number;
    periodId?: number;
    bypassRestrictions?: boolean;
    hashes?: string[];
    selectedPeople?: PsData[] | FlexReportUnregisteredTableData[];
    canOverride?: boolean;
  }): Promise<void> {
    const actionInfo: FlexActionInfo = {
      type: 'register',
      verb: 'Register',
      action: 'Registration',
    };

    return this._doFlexAction({
      actionInfo,
      activityId,
      periodId,
      bypassRestrictions,
      hashes,
      selectedPeople,
      canOverride,
    });
  }

  public async registerToHomeroom({
    periodId,
    hashes,
  }: {
    periodId: number;
    hashes: string[];
  }): Promise<{ successHashes: string[] }> {
    const userlists = await this._rootService.addLoadingPromise(this._userLists.getAllLists());

    const homeroomLists = userlists.filter(list => list.data.isHomeroom);

    if (homeroomLists.length === 0) {
      const modalRef = await this.alertModal.open({
        modalType: SystemAlertModalType.ERROR,
        heading: FtmReportsUnRegMessages.USERLIST_MODAL_HEADING,
        message: FtmReportsUnRegMessages.USERLIST_MODAL_MESSAGE,
        confirmActionBtn: FtmReportsUnRegMessages.USERLIST_MODAL_CONFIRM,
      });

      const response = await modalRef.afterClosed().toPromise();

      if (response?.type === SystemAlertCloseEvents.CONFIRM) {
        this._router.navigate([PeopleRoute.ROOT, PeopleRoute.USER_LISTS]);
      }

      return {
        successHashes: [],
      };
    }

    const modalRef = await this.alertModal.open({
      modalType: SystemAlertModalType.DEFAULT,
      heading: FtmReportsUnRegMessages.UNREGISTERED_MODAL_HEADING,
      message: `${hashes.length} students will be registered to their homeroom teacher's activity.`,
      closeBtn: FtmReportsUnRegMessages.UNREGISTERED_MODAL_CLOSE,
      confirmActionBtn: FtmReportsUnRegMessages.UNREGISTERED_MODAL_CONFIRM,
      icon: 'mg-flextime-menu-o',
    });

    const response = await modalRef.afterClosed().toPromise();

    if (response?.type !== SystemAlertCloseEvents.CONFIRM) {
      return {
        successHashes: [],
      };
    }

    const { errors } = await this._rootService.addLoadingPromise(
      this._ftRegistration.registerToHomeroom({
        periodId,
        hashes,
      }),
    );

    const successHashes: string[] = [];
    const successes = hashes.filter(h => !errors.some(e => e.personHash === h));
    successHashes.push(...successes);

    if (errors.length > 0) {
      const errorsData: PsSummaryErrorsData[] = errors.map(e => ({
        status: 'error',
        name: e.personName,
        hash: e.personHash,
        reasons: [e.message],
      }));

      const bypassSuccesses = await this._peopleSelector.openSummary({
        title: FtmReportsUnRegMessages.ERROR_MODAL_TITLE,
        message: FtmReportsUnRegMessages.ERROR_MODAL_MESSAGE,
        subMessage: '',
        submitLabel: FtmReportsUnRegMessages.ERROR_MODAL_CLOSE,
        disableCloseConfirmDialog: true,
        errorsData,
        displayOnly: true,
      });

      successHashes.push(...bypassSuccesses);
    } else {
      this.snackbar.open({
        type: 'success',
        message: FtmReportsUnRegMessages.SUCCESS_SNACKBAR_MESSAGE,
      });
    }

    return {
      successHashes,
    };
  }

  private async _doFlexAction({
    actionInfo,
    activityId,
    periodId,
    bypassRestrictions = false,
    hashes,
    selectedPeople,
    canOverride,
  }: {
    actionInfo: FlexActionInfo;
    activityId?: number;
    periodId?: number;
    bypassRestrictions?: boolean;
    hashes?: string[];
    selectedPeople?: PsData[] | FlexReportUnregisteredTableData[];
    canOverride?: boolean;
  }): Promise<void> {
    try {
      let targetHashes = hashes;

      if (!hashes && !bypassRestrictions) {
        targetHashes = await this._validateRegistration(
          periodId,
          activityId,
          actionInfo,
          this._getSelectedHashes(selectedPeople),
          selectedPeople,
          canOverride,
        );
        if (targetHashes?.length) bypassRestrictions = true;
      }
      if (!targetHashes?.length) return;
      const { assignments, errors } = await this._rootService.addLoadingPromise(
        this._ftRegistration.assign(activityId, targetHashes, actionInfo.type === 'register', bypassRestrictions),
      );
      if (assignments.length > 0 && errors.length === 0) {
        this.snackbar.success(`Student(s) successfully ${actionInfo.type}ed`);
      }
      if (errors.length) {
        const overidePeopleHashes = await this._handleAssignErrors(actionInfo, assignments, errors, selectedPeople);
        if (overidePeopleHashes.length) {
          return await this._doFlexAction({
            actionInfo,
            activityId,
            periodId,
            bypassRestrictions: true,
            hashes: overidePeopleHashes,
            canOverride,
          });
        }
      }
    } catch (error) {
      this.snackbar.open({
        type: 'error',
        message: error.message || 'Something went wrong, please try again later',
      });
    }
  }

  private async _validateRegistration(
    periodId: number,
    activityId: number,
    actionInfo: FlexActionInfo,
    hashes,
    selectedPeople,
    canOverridePerm?: boolean,
  ): Promise<string[]> {
    const singlePersonSubmission = hashes.length === 1;

    const { success, existingRegistrations, restrictionsErrors } = await this._rootService.addLoadingPromise(
      this._ftRegistration.validate(periodId, hashes, activityId),
    );

    if (success.length === hashes.length) return hashes;

    if (singlePersonSubmission) {
      return await this._handlePersonValidationErrors(
        existingRegistrations,
        restrictionsErrors,
        actionInfo,
        hashes,
        canOverridePerm,
      );
    } else {
      if (success.length) {
        await this._doFlexAction({
          actionInfo,
          activityId,
          periodId,
          bypassRestrictions: true,
          hashes: success,
          canOverride: canOverridePerm,
        });
      }

      const errorsData = this._makeErrorData(
        success,
        existingRegistrations,
        restrictionsErrors,
        actionInfo,
        selectedPeople,
        canOverridePerm,
      );

      const displayOnly = errorsData.every(item => item.readonly) ?? this._flexPermissions.isFlexTimeAdmin();

      const overrideHashes = await this._peopleSelector.openSummary({
        message: `could not be ${actionInfo.verb}ed`,
        title: `${actionInfo.action} summary`,
        subMessage: !displayOnly ? `Select the people you want to “${actionInfo.verb}” to replace the activity` : '',
        submitLabel: actionInfo.verb,
        errorsData,
        displayOnly: displayOnly,
      });
      if (overrideHashes.length) {
        await this._doFlexAction({
          actionInfo,
          activityId,
          periodId,
          bypassRestrictions: true,
          hashes: overrideHashes,
        });
      }
    }
  }

  private async _handlePersonValidationErrors(
    registrations: ft_activity_pb.ExistingRegistration.AsObject[],
    restrictions: RestrictionErrorMinimal[],
    actionInfo: FlexActionInfo,
    hashes: string[],
    canOverride?: boolean,
  ) {
    const canOveride = canOverride ?? this._flexPermissions.isFlexTimeAdmin();
    let hardStop = false;
    const overrides: null | boolean[] = [null, null];
    if (restrictions && restrictions.length > 0) {
      for (const restriction of restrictions) {
        if (hardStop) break;
        const { systemAlertOptions } = this._createSystemAlertOptions(actionInfo, restriction);
        const systemAlertModal = await this.alertModal.open(systemAlertOptions);
        const { type } = await systemAlertModal.afterClosed().toPromise();
        if (type === SystemAlertCloseEvents.CLOSE) overrides[0] = true;
        else hardStop = true;
      }
    }
    if (registrations && registrations.length > 0 && !hardStop) {
      const overrideHashes = await this._existingRegistrationError(actionInfo, canOveride, hashes, registrations[0]);
      if (overrideHashes?.length > 0) overrides[1] = true;
    }
    const checks = overrides.filter(type => typeof type === 'boolean');
    if (checks.length > 0) return checks.every(v => v) ? hashes : [];
    else return [];
  }

  private async _existingRegistrationError(
    actionInfo: FlexActionInfo,
    canOveride: boolean,
    hashes: string[],
    {
      activityName,
      activityTeacherName,
      registeredBy,
      activityLocation,
      studentName,
      type: registrationType,
    }: ft_activity_pb.ExistingRegistration.AsObject,
  ): Promise<string[]> {
    const isAssigned = registrationType === ft_activity_pb.RegistrationType.ASSIGNMENT;
    const canContinue = !this._determineReadonly(isAssigned, canOveride);
    const studentStatus = isAssigned ? 'assigned' : 'registered';
    const systemAlertModal = await this.alertModal.open({
      modalType: canContinue ? SystemAlertModalType.WARNING : SystemAlertModalType.ERROR,
      heading: `This student is already ${studentStatus} to another activity`,
      message: canContinue
        ? 'Would you like to replace their current activity?'
        : `This student is already ${studentStatus} to another activity`,
      confirmActionBtn: canContinue ? actionInfo.verb : 'Close',
      closeBtn: canContinue && 'Cancel',
      detailedMessage: [
        [`${actionInfo.verb}ed by`, registeredBy],
        ['Student', studentName],
        ['Activity', activityName],
        ['Location', activityLocation],
        ['Teacher', activityTeacherName],
      ],
    });
    const { type } = await systemAlertModal.afterClosed().toPromise();
    if (type === SystemAlertCloseEvents.CONFIRM) {
      return canContinue ? hashes : [];
    }
  }

  private _makeErrorData(
    success: string[],
    data: ft_activity_pb.ExistingRegistration.AsObject[],
    restrictions: RestrictionErrorMinimal[],
    actionInfo: FlexActionInfo,
    selectedPeople: PsData[] | FlexReportUnregisteredTableData[],
    override: boolean,
  ): PsSummaryErrorsData[] {
    const successes = success.map(hash => {
      const person = this._getSelected(selectedPeople, hash);
      return {
        hash: this._getHash(person),
        name: this._getPersonName(person),
        status: 'success',
        reasons: [`${actionInfo.verb}ed`],
      } as PsSummaryErrorsData;
    });
    const restrictionsErrors = restrictions.map(restrictionError => {
      const {
        personHash: hash,
        personName: name,
        noPartyDetails,
        reasons,
      } = this._handleRestrictionError(restrictionError, actionInfo);

      return {
        status: 'error',
        hash,
        name,
        reasons,
        advancedTooltip: noPartyDetails,
      } as PsSummaryErrorsData;
    });
    const errors = data.map(
      ({ studentHash, studentName, registeredBy, activityName, activityTeacherName, activityLocation, type }) =>
        ({
          hash: studentHash,
          name: studentName,
          status: 'error',
          readonly: this._determineReadonly(type === ft_activity_pb.RegistrationType.ASSIGNMENT, override),
          reasons: [
            `${type === ft_activity_pb.RegistrationType.ASSIGNMENT ? 'Assigned' : 'Registered'} to another activity`,
          ],
          advancedTooltip: [
            [type === ft_activity_pb.RegistrationType.ASSIGNMENT ? 'Assigned by' : 'Registered by', registeredBy],
            ['Activity', activityName],
            ['Teacher', activityTeacherName],
            ['Location', activityLocation],
          ],
        } as PsSummaryErrorsData),
    );
    return [...restrictionsErrors, ...errors, ...successes];
  }

  private _handleRestrictionError(error: RestrictionErrorMinimal, actionInfo: FlexActionInfo) {
    const { code, personHash, personName, noPartyInfos } = error;
    let noPartyDetails: [string, string][] | undefined;
    let message: string = RESTRICTION_ERRORS[code].message || `${actionInfo.verb} denied`;
    if (code === 'NO_PARTY') {
      message = personName + ' ' + RESTRICTION_ERRORS[code].message;
      if (noPartyInfos?.length) {
        noPartyDetails = [];
        for (const noPartyInfo of noPartyInfos) {
          noPartyDetails.push(['No party group', noPartyInfo.partyName]);
          noPartyDetails.push(['Conflicting user(s)', noPartyInfo.conflictPersonNames.join(', ')]);
        }
      } else {
        console.error('Missing no party info for registration error');
      }
    }
    return {
      personHash,
      personName,
      message,
      noPartyDetails,
      reasons: [RESTRICTION_ERRORS[code].title || 'Restriction denied'],
      heading: ACTIVITY_RESTRICTION_ERRORS_TITLES[code].header || `${actionInfo.action} denied`,
    };
  }

  private async _handleAssignErrors(
    actionInfo: FlexActionInfo,
    successes: Registration[],
    errors: RestrictionErrorMinimal[],
    selectedPeople: PsData[] | FlexReportUnregisteredTableData[],
  ): Promise<string[]> {
    const successMapped = successes.map(({ personHash }) => {
      const person = this._getSelected(selectedPeople, personHash);
      return {
        status: 'success',
        hash: this._getHash(person),
        name: this._getPersonName(person),
        reasons: [`${actionInfo.verb}ed`],
      } as PsSummaryErrorsData;
    });
    const errorsMapped: PsSummaryErrorsData[] = errors.map(
      ({ personHash, personName, code }) =>
        ({
          status: 'error',
          hash: personHash,
          name: personName,
          reasons: [RESTRICTION_ERRORS[code].title || 'Assignment denied'],
        } as PsSummaryErrorsData),
    );
    if (successes.length === 0 && errors.length === 1) {
      const { personHash, systemAlertOptions } = this._createSystemAlertOptions(actionInfo, errors[0]);
      const systemAlertModal = await this.alertModal.open(systemAlertOptions);

      const systemAlertModalData = await systemAlertModal.afterClosed().toPromise();

      switch (systemAlertModalData?.type) {
        case SystemAlertCloseEvents.CLOSE:
          return [personHash];
        case SystemAlertCloseEvents.CONFIRM:
          return [];
      }
    } else {
      const canOverride = this._flexPermissions.isFlexTimeAdmin();

      const hashes = await this._peopleSelector.openSummary({
        message: `could not be ${actionInfo.type}ed`,
        title: `${actionInfo.action} summary`,
        subMessage: canOverride ? `Select the people you want to “${actionInfo.verb}”` : '',
        submitLabel: actionInfo.verb,
        errorsData: [...successMapped, ...errorsMapped],
        displayOnly: !canOverride,
      });
      return hashes;
    }
  }

  private _createSystemAlertOptions(actionInfo: FlexActionInfo, error: RestrictionErrorMinimal) {
    const {
      personHash,
      noPartyDetails: detailedMessage,
      heading,
      message,
    } = this._handleRestrictionError(error, actionInfo);

    // close and confirm buttons are "mislabeled" to match design requirements
    // design wants the filled button on the right (normally confirm) to be the close action
    // and the outlined button on the left to be the override action
    return {
      personHash,
      systemAlertOptions: {
        modalType: SystemAlertModalType.ERROR,
        heading,
        message,
        confirmActionBtn: 'Close',
        closeBtn: `${actionInfo.verb} anyway`,
        detailedMessage,
      },
    };
  }

  private _getSelected(selectedPeople: (PsData | FlexReportUnregisteredTableData)[], personHash: string) {
    const selectedPerson = selectedPeople.find((person: PsData | FlexReportUnregisteredTableData): person is PsData => {
      return this._getHash(person) === personHash;
    });

    if (!selectedPerson) throw new Error('Person not found');

    return selectedPerson;
  }

  private _getSelectedHashes(selectedPeople: PsData[] | FlexReportUnregisteredTableData[]) {
    return (selectedPeople as Array<PsData | FlexReportUnregisteredTableData>)
      .map(person => this._getHash(person))
      .filter(hash => hash !== null);
  }

  private _getPersonName(person: PsData | FlexReportUnregisteredTableData): string {
    if ('firstName' in person && 'lastName' in person) {
      return `${person.firstName} ${person.lastName}`;
    } else if ('displayName' in person) {
      return person.displayName;
    }
    return '';
  }

  private _getHash(person: PsData | FlexReportUnregisteredTableData): string | null {
    if ('hash' in person) {
      return person.hash;
    } else if ('personHash' in person) {
      return person.personHash;
    }
    return null;
  }

  // table entry is never readonly for admins
  // readonly when assigned and not admin
  // currently override is unused, however flex requirements change a lot, so I'm leaving it because I'm anticipating needing it in the future again

  // | Admin | Assigned | readonly |
  // |-------|----------|----------|
  // | False | False    | False    |
  // | False | True     | True     |
  // | True  | False    | False    |
  // | True  | True     | False    |
  private _determineReadonly(assigned: boolean, override?: boolean): boolean {
    return !this._flexPermissions.isFlexTimeAdmin() && assigned;
  }
}
